import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle, X, SkipForward, RefreshCw } from 'lucide-react'
import { Button } from './Button'

export interface DuplicateFile {
  filename: string
  existingDate: string
  newDate: string
}

interface DuplicateWarningModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  duplicates: DuplicateFile[]
  onSkipAll: () => void
  onReplaceAll: () => void
  onSkipSelected: (filenames: string[]) => void
}

export function DuplicateWarningModal({
  open,
  onOpenChange,
  duplicates,
  onSkipAll,
  onReplaceAll,
}: DuplicateWarningModalProps) {
  if (duplicates.length === 0) return null

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-zinc-900 rounded-lg shadow-xl border border-zinc-800 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <Dialog.Title className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Duplicate Files Found
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-zinc-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="p-4 space-y-4">
            <p className="text-sm text-zinc-400">
              {duplicates.length === 1
                ? 'The following file already exists on the device:'
                : `${duplicates.length} files already exist on the device:`}
            </p>

            <div className="max-h-48 overflow-auto bg-zinc-800/50 rounded-lg p-3 space-y-2">
              {duplicates.map((dup) => (
                <div
                  key={dup.filename}
                  className="flex items-center justify-between text-sm py-1 border-b border-zinc-700 last:border-0"
                >
                  <span className="text-zinc-300 font-mono text-xs truncate flex-1 mr-2">
                    {dup.filename}
                  </span>
                  <span className="text-zinc-500 text-xs whitespace-nowrap">
                    {dup.existingDate === dup.newDate ? (
                      <span className="text-yellow-400">Same date</span>
                    ) : (
                      <>
                        <span className="text-zinc-500">{dup.existingDate}</span>
                        <span className="text-zinc-600 mx-1">→</span>
                        <span className="text-zinc-400">{dup.newDate}</span>
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>

            <div className="bg-zinc-800/30 rounded-lg p-3 text-sm text-zinc-400">
              <p className="font-medium text-zinc-300 mb-1">What would you like to do?</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong className="text-zinc-300">Skip:</strong> Keep existing files, don't upload duplicates</li>
                <li><strong className="text-zinc-300">Replace:</strong> Upload new files, overwriting existing ones</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-3 p-4 border-t border-zinc-800">
            <Button
              variant="secondary"
              onClick={() => {
                onSkipAll()
                onOpenChange(false)
              }}
              className="flex-1"
            >
              <SkipForward className="w-4 h-4 mr-2" />
              Skip All
            </Button>
            <Button
              onClick={() => {
                onReplaceAll()
                onOpenChange(false)
              }}
              className="flex-1"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Replace All
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
